import clsx from "clsx";
import TwoSidebars from "../../layouts/TwoSidebars/TwoSidebars";
import {
  MdOutlineNavigateBefore,
  MdPlayArrow,
  MdRefresh,
  MdSettings,
} from "react-icons/md";
import ButtonGroup from "../../components/ButtonGroup/ButtonGroup";
import { useTranslation } from "react-i18next";
import PageTitle from "../../components/PageTitle/PageTitle";
import { BUTTON_STYLES, ICON_SIZES } from "../../helpers/constants";
import { useNavigate } from "react-router-dom";
import useMobile from "../../hooks/useMobile";

import Stepper from "../../components/Stepper/Stepper";
import { useMemo, useState } from "react";
import { showNotification, sleep } from "../../helpers/utils";
import { useQueryClient } from "@tanstack/react-query";
import SettingsBlocks from "../../components/SettingsBlocks/SettingsBlocks";
import useGeneralOptionsDropdownMenu from "../../hooks/useGeneralOptionsDropdownMenu";
import { AnimatePresence } from "framer-motion";
import useAllTransactions from "src/hooks/useAllTransactions";
import useCurrentUser from "src/hooks/useCurrentUser";
import { EButtonKind } from "src/components/Button/ButtonTypes";
import LoadingWithTextPage from "src/components/LoadingWithText/LoadingWithTextPage";

const ResetCachePage: React.FC = () => {
  const generalOptionsDropdown = useGeneralOptionsDropdownMenu();
  const {
    isLoadingAllTransactions,
    isLoadingCustomizedCategories,
    isLoadingCustomizedSources,
  } = useAllTransactions();
  const { isLoadingAccounts } = useCurrentUser();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isMobile } = useMobile();
  const [isCleaningCache, setIsCleaningCache] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const [cleaningNotifications, setCleaningNotifications] = useState<any>({
    done: false,
    isLoading: false,
    started: false,
  });

  const [cleaningServiceWorker, setCleaningServiceWorker] = useState<any>({
    done: false,
    isLoading: false,
    started: false,
  });

  const [cleaningLocalStorage, setCleaningLocalStorage] = useState<any>({
    done: false,
    isLoading: false,
    started: false,
  });

  const [cleaningReactQuery, setCleaningReactQuery] = useState<any>({
    done: false,
    isLoading: false,
    started: false,
  });

  const clearCache = async () => {
    setIsCleaningCache(true);

    setCleaningNotifications({ isLoading: true, done: false });
    setCleaningServiceWorker({ isLoading: false, done: false });
    setCleaningReactQuery({ isLoading: false, done: false });
    setCleaningLocalStorage({ isLoading: false, done: false });

    // if (isPwa()) {
    // }
    if ("clearAppBadge" in navigator) {
      await (navigator as any).clearAppBadge();
    }
    await sleep(1000);
    setCleaningNotifications({ isLoading: false, done: true });

    setCleaningServiceWorker({ isLoading: true, done: false });
    await caches.keys().then(function (cacheNames) {
      return Promise.allSettled(
        cacheNames.map(async function (cacheName) {
          return await caches.delete(cacheName);
        })
      );
    });
    await sleep(1000);

    setCleaningServiceWorker({ isLoading: false, done: true });

    setCleaningLocalStorage({ isLoading: true, done: false });
    await sleep(1000);
    setCleaningLocalStorage({ isLoading: false, done: true });

    setCleaningReactQuery({ isLoading: true, done: false });
    await queryClient.refetchQueries();
    await sleep(1000);
    setCleaningReactQuery({ isLoading: false, done: true });
    showNotification({
      message: t("Caché borrada correctamente"),
      kind: "success",
    });

    setIsCleaningCache(false);
    // window?.location?.reload?.();
  };

  const isLoadingSomething = useMemo(
    () =>
      [
        !!isLoadingAllTransactions,
        !!isLoadingAccounts,
        !!isLoadingCustomizedSources,
      ]?.includes(true),
    [isLoadingAllTransactions, isLoadingAccounts, isLoadingCustomizedSources]
  );

  return (
    <TwoSidebars
      right={
        isMobile ? null : (
          <SettingsBlocks layout="vertical" columns={isMobile ? 2 : 1} />
        )
      }
      isLoading={isLoadingSomething}
    >
      <div
        className={clsx([
          "flex",
          "flex-col",
          "w-full",
          "gap-2",
          "md:gap-2",
          "h-full",
        ])}
      >
        <AnimatePresence>
          {isLoadingSomething ? (
            <LoadingWithTextPage />
          ) : (
            <>
              <PageTitle
                titleLink="/settings"
                icon={<MdSettings size={ICON_SIZES.MD} />}
                title={t("settings_page.title")}
                subtitle={t("Borrar caché")}
                rightSide={
                  <ButtonGroup
                    align="right"
                    gap={1}
                    buttons={[
                      {
                        icon: <MdOutlineNavigateBefore size={ICON_SIZES.MD} />,
                        onClick: () => {
                          navigate("/settings/");
                        },
                        className: isMobile ? BUTTON_STYLES.HEADING : "",
                      },
                      generalOptionsDropdown,
                    ]}
                  />
                }
              />

              <div className={clsx(["flex", "flex-col", "gap-6", "w-full"])}>
                <div
                  className={clsx([
                    "flex",
                    "w-full",
                    "text-sm",
                    "md:text-base",
                  ])}
                >
                  La limpieza de caché puede tardar un momento. Te recomendamos
                  que no abandones esta página hasta que la limpieza haya
                  finalizado.
                </div>

                <Stepper
                  steps={[
                    {
                      title: "Notificaciones",
                      description: "Limpiar badges de notificaciones",
                      ...cleaningNotifications,
                    },
                    {
                      title: "Caché PWA",
                      description: "Limpiar caché del service worker",
                      ...cleaningServiceWorker,
                    },
                    {
                      title: "Local Storage",
                      description: "Limpiar almacenamiento local",
                      ...cleaningLocalStorage,
                    },
                    {
                      title: "Caché API",
                      description: "Limpiar caché del API",
                      ...cleaningReactQuery,
                    },
                  ]}
                  started={isCleaningCache}
                />

                <div
                  className={clsx([
                    "flex",
                    "w-full",
                    "text-sm",
                    "md:text-base",
                  ])}
                >
                  También puedes recargar la aplicación, pero esto no borrará la
                  totalidad de la caché almacenada.
                </div>

                <ButtonGroup
                  align="right"
                  fullWidth
                  gap={1}
                  buttons={[
                    {
                      icon: <MdRefresh size={ICON_SIZES.MD} />,
                      onClick: () => {
                        showNotification({
                          message: t("Recargando la aplicación"),
                          kind: "info",
                        });
                        window?.location?.reload?.();
                      },
                      text: "Recargar",
                      kind: EButtonKind.NORMAL,
                      disabled: isCleaningCache,
                    },
                    {
                      icon: <MdPlayArrow size={ICON_SIZES.MD} />,
                      onClick: async () => {
                        await clearCache();
                      },
                      kind: EButtonKind.SAVE,
                      text: "Iniciar",
                      isLoading: isCleaningCache,
                      disabled: isCleaningCache,
                    },
                  ]}
                />
              </div>
            </>
          )}
        </AnimatePresence>
      </div>
    </TwoSidebars>
  );
};

export default ResetCachePage;
